/**
 * Dates on the promoter portal. An agency's bar and commissions are dated in THAT agency's time zone (the US
 * has several, design A1; CLAUDE.md "Local time"), with the zone's US abbreviation so a promoter in Texas
 * reading a Florida agency's dates knows whose calendar they are. The words follow the UI language; the
 * abbreviation is always the US one (`EDT`, never `GMT-4`, which is what Spanish Intl would print).
 */
function validZone(zone: string | null | undefined): string {
  if (!zone) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return 'UTC';
  }
}

function tag(locale: string): string {
  return locale === 'es' ? 'es-US' : 'en-US';
}

/** `Oct 4, 2026 EDT` / `4 oct 2026 EDT`; '' without a date. */
export function formatInZone(iso: string | null | undefined, zone: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  const timeZone = validZone(zone);
  const day = new Intl.DateTimeFormat(tag(locale), { timeZone, year: 'numeric', month: 'short', day: 'numeric' })
    .format(when)
    .replace(/\./g, '')
    .replace(/ de /g, ' ');
  const abbr =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(when).find((p) => p.type === 'timeZoneName')?.value ??
    timeZone;
  return `${day} ${abbr}`;
}

/**
 * An EXCLUSIVE end instant (a bar's `bar_ends_at`: the commission window covers up to, not including, it) as
 * the last day it includes, in that zone: 00:00 Apr 4 New York time reads `Apr 3, 2027 EDT`.
 */
export function lastDayInZone(iso: string | null | undefined, zone: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return formatInZone(new Date(t - 1).toISOString(), zone, locale);
}

/** A plain calendar date (`2026-10-05`, e.g. a payout's paid_on): that day, in no zone. */
export function formatDay(date: string | null | undefined, locale: string): string {
  const m = date ? /^(\d{4})-(\d{2})-(\d{2})/.exec(date) : null;
  if (!m) return '';
  const when = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return new Intl.DateTimeFormat(tag(locale), { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' })
    .format(when)
    .replace(/\./g, '')
    .replace(/ de /g, ' ');
}

/** A moment in the viewer's own zone (when he signed, uploaded, requested): date only. */
export function formatLocalDay(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  return new Intl.DateTimeFormat(tag(locale), { year: 'numeric', month: 'short', day: 'numeric' })
    .format(when)
    .replace(/\./g, '')
    .replace(/ de /g, ' ');
}
